import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FaCheck} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import ConfirmActionModal from "../components/ConfirmDeleteModal";
import PageHeading from "../components/PageHeading";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState([]);

  const API_KEY =
    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const API_URL = "https://api.muktihospital.com/api/notifications";

  // Fetch all notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications([...(data.notifications || [])]); // Ensure new array reference
        setSelectedRows(new Array(data.notifications?.length || 0).fill(false));
      } else {
        toast.error("Failed to fetch notifications.");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching notifications: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Filter notifications based on search term
  const filterNotifications = () => {
    return notifications.filter((notification) => {
      const type = notification.type?.toLowerCase() || "";
      const message = notification.message?.toLowerCase() || "";
      return (
        type.includes(searchTerm.toLowerCase()) ||
        message.includes(searchTerm.toLowerCase())
      );
    });
  };

  // Pagination logic
  const filteredData = filterNotifications();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Debug state changes
  useEffect(() => {
    // console.log("Notifications updated:", notifications);
    // console.log("Current Page:", currentPage);
    // console.log("Filtered Data:", filteredData);
    // console.log("Total Pages:", totalPages);
    // console.log("Current Data:", currentData);
  }, [notifications, currentPage, filteredData, totalPages, currentData]);

  // Adjust page if currentPage exceeds totalPages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      // console.log(`Adjusting page from ${currentPage} to ${totalPages}`);
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      // console.log("No data, resetting to page 1");
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Update header checkbox state

  useEffect(() => {
    if (currentData.length > 0) {
      setIsHeaderChecked(
        currentData.every((_, idx) => {
          const globalIndex = notifications.findIndex(
            (notif) => notif.id === currentData[idx].id
          );
          return globalIndex !== -1 && selectedRows[globalIndex];
        })
      );
    } else {
      setIsHeaderChecked(false);
    }
  }, [selectedRows, currentData, notifications]);

  // Reset selected rows when notifications change
  useEffect(() => {
    setSelectedRows(new Array(notifications.length).fill(false));
    setIsHeaderChecked(false);
  }, [notifications]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      // console.log(`Changing page to ${page}`);
      setCurrentPage(page);
    }
  };

  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = [...selectedRows];
    currentData.forEach((_, idx) => {
      const globalIndex = notifications.findIndex(
        (notif) => notif.id === currentData[idx].id
      );
      if (globalIndex !== -1) {
        newSelectedRows[globalIndex] = !isHeaderChecked;
      }
    });
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!isHeaderChecked);
  };

  const handleRowCheckboxChange = (globalIndex) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[globalIndex] = !newSelectedRows[globalIndex];
    setSelectedRows(newSelectedRows);
  };

  // Delete single or multiple notifications
  const handleDeleteNotifications = async (ids) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    if (idArray.length === 0) return;

    // console.log("Deleting notifications with IDs:", idArray);

    try {
      const deletePromises = idArray.map((id) =>
        fetch(`${API_URL}/delete/${id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
          },
          credentials: "include",
        })
      );
      const responses = await Promise.allSettled(deletePromises);

      const failedRequests = responses.filter(
        (result) => result.status === "rejected" || !result.value.ok
      );

      if (failedRequests.length > 0) {
        failedRequests.forEach((result, index) => {
          console.error(`Delete failed for ID ${idArray[index]}:`, result.reason);
        });
        throw new Error("Some delete requests failed.");
      }

      // Optimistically update notifications
      const updatedNotifications = notifications.filter(
        (notification) => !idArray.includes(String(notification.id))
      );

      // Calculate new total pages based on filtered data
      const newFilteredData = updatedNotifications.filter((notification) => {
        const type = notification.type?.toLowerCase() || "";
        const message = notification.message?.toLowerCase() || "";
        return (
          type.includes(searchTerm.toLowerCase()) ||
          message.includes(searchTerm.toLowerCase())
        );
      });
      const newTotalPages = Math.ceil(newFilteredData.length / itemsPerPage);

      // Adjust current page
      let newCurrentPage = currentPage;
      if (currentPage > newTotalPages && newTotalPages > 0) {
        newCurrentPage = newTotalPages;
      } else if (newTotalPages === 0) {
        newCurrentPage = 1;
      }

      // Update state
      setNotifications([...updatedNotifications]);
      setCurrentPage(newCurrentPage);
      setSelectedRows(new Array(updatedNotifications.length).fill(false));
      setIsHeaderChecked(false);

      toast.success(
        `Notification${idArray.length > 1 ? "s" : ""} deleted successfully`
      );

      // Fallback: Refetch data to ensure consistency
      await fetchNotifications();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete notification(s). Refetching data...");
      await fetchNotifications();
    } finally {
      closeDeleteModal();
    }
  };

  const openDeleteModal = (ids) => {
    setSelectedNotificationIds(ids);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedNotificationIds([]);
    setShowDeleteModal(false);
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Settings & Config"},
    { label: "Notifications" },
  ];


  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Notifications" breadcrumbs={breadcrumbs} />
      </div>
      <ConfirmActionModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteNotifications}
        ids={selectedNotificationIds}
        entityName="notification"
        title="Confirm Delete"
        message={
          selectedNotificationIds.length > 1
            ? `Do you really want to delete ${selectedNotificationIds.length} notifications?`
            : "Do you really want to delete this notification?"
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
      <div className="mt-6">
        <div className="bg-white shadow-sm rounded-lg">
          <div className="flex flex-col gap-3 sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <div className="inline-flex items-center gap-3 shrink-0">
              <h2 className="text-base font-medium text-gray-700">
                Notification List
              </h2>
              {selectedRows.some((isSelected) => isSelected) && (
                <button
                  onClick={() => {
                    const selectedIds = notifications
                      .map((notif, index) =>
                        selectedRows[index] ? notif.id : null
                      )
                      .filter((id) => id !== null);
                    openDeleteModal(selectedIds);
                  }}
                  className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                  title="Delete selected rows"
                >
                  <RiDeleteBin6Line />
                </button>
              )}
            </div>
            <div className="inline-flex flex-col sm:flex-row items-start sm:items-center justify-end gap-3 flex-1">
              {loading ? (
                <Skeleton height={40} width={192} />
              ) : (
                <input
                  type="text"
                  className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
                  placeholder="Search by type, message"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              )}
            </div>
          </div>
          {/* Table Area */}
          <div className="overflow-x-auto">
            {loading ? (
              <Skeleton count={itemsPerPage} height={50} />
            ) : (
              <table className="min-w-[1000px] w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-[#FBFBFD]">
                    <th className="p-3 text-left">
                      <div className="relative left-3 -top-[1px]">
                        <input
                          type="checkbox"
                          id="header-checkbox"
                          className="hidden peer"
                          checked={isHeaderChecked}
                          onChange={handleHeaderCheckboxChange}
                        />
                        <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                          <FaCheck className="text-slate-100" />
                        </span>
                        <label
                          htmlFor="header-checkbox"
                          className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                        ></label>
                      </div>
                    </th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">
                      SL No
                    </th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">
                      Type
                    </th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">
                      Message
                    </th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="p-3 text-center text-sm font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center p-4">
                        No notifications available
                      </td>
                    </tr>
                  ) : (
                    currentData.map((notification, index) => {
                      const serialNumber = indexOfFirstItem + index + 1;
                      const notificationId =
                        notification.id != null
                          ? String(notification.id)
                          : "N/A";
                      const globalIndex = notifications.findIndex(
                        (notif) => notif.id === notification.id
                      );
                      return (
                        <tr
                          key={notificationId}
                          className="border-b hover:bg-gray-50"
                        >
                          <td className="p-3">
                            <div className="relative left-3 -top-[3px]">
                              <input
                                type="checkbox"
                                id={`notif-${globalIndex}`}
                                className="hidden peer"
                                checked={selectedRows[globalIndex] || false}
                                onChange={() =>
                                  handleRowCheckboxChange(globalIndex)
                                }
                              />
                              <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                                <FaCheck className="text-slate-100" />
                              </span>
                              <label
                                htmlFor={`notif-${globalIndex}`}
                                className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                              ></label>
                            </div>
                          </td>
                          <td className="p-3 text-sm">{serialNumber}</td>
                          <td className="p-3 text-sm">
                            {notification.type || "N/A"}
                          </td>
                          <td className="p-3 text-sm">
                            {notification.message || "N/A"}
                          </td>
                          <td className="p-3 text-sm">
                            {notification.readStatus ? "Read" : "Unread"}
                          </td>
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() =>
                                  openDeleteModal([notificationId])
                                }
                                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white rounded-full flex items-center justify-center transition"
                              >
                                <RiDeleteBin6Line />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-5 text-end">
              <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                <li>
                  <button
                    className="table-pagination-item disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <MdKeyboardArrowLeft />
                  </button>
                </li>
                {totalPages <= 10
                  ? [...Array(totalPages)].map((_, i) => (
                      <li key={i}>
                        <button
                          className={`table-pagination-item ${
                            currentPage === i + 1
                              ? "bg-M-primary-color text-white"
                              : ""
                          }`}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))
                  : (() => {
                      const pages = [];
                      const firstPages = 5;
                      const lastPages = 3;

                      for (
                        let i = 1;
                        i <= Math.min(firstPages, totalPages);
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg-M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      if (totalPages > firstPages + lastPages) {
                        pages.push(
                          <li key="ellipsis" className="table-pagination-item">
                            ...
                          </li>
                        );
                      }

                      for (
                        let i = Math.max(
                          firstPages + 1,
                          totalPages - lastPages + 1
                        );
                        i <= totalPages;
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg-M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      return pages;
                    })()}
                <li>
                  <button
                    className="table-pagination-item"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <MdKeyboardArrowRight />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPage;